import React from 'react';
import LegalPageLayout from '../../components/Legal/LegalPageLayout';

const sections = [
  { id: 'what-are-cookies', label: 'What Are Cookies' },
  { id: 'types', label: 'Types We Use' },
  { id: 'cookie-table', label: 'Cookie List' },
  { id: 'third-party', label: 'Third-Party Cookies' },
  { id: 'manage', label: 'Managing Cookies' },
  { id: 'legal-basis', label: 'Legal Basis' },
  { id: 'contact', label: 'Contact' },
];

const CookiePolicy = () => (
  <LegalPageLayout
    title="Cookie Policy"
    subtitle="How CRM Automax uses cookies and similar tracking technologies."
    lastUpdated="May 7, 2025"
    sections={sections}
  >
    <div className="legal-section" id="what-are-cookies" data-section>
      <h2>1. What Are Cookies?</h2>
      <p>
        Cookies are small text files placed on your device (computer, tablet, or mobile) when you
        visit a website. They are widely used to make websites work efficiently, remember your
        preferences, and provide information to website owners.
      </p>
      <p>
        Similar technologies include <strong>localStorage</strong> (browser storage), session tokens,
        and tracking pixels. This policy covers all such technologies used on the CRM Automax platform.
      </p>
      <div className="legal-highlight">
        <p>
          <strong>Your Control:</strong> You can manage your cookie preferences at any time using our
          Cookie Preference Centre (accessible via the cookie banner or the link in our footer). You
          may also manage cookies through your browser settings.
        </p>
      </div>
    </div>

    <div className="legal-section" id="types" data-section>
      <h2>2. Types of Cookies We Use</h2>
      <h3>2.1 Strictly Necessary Cookies</h3>
      <p>
        These cookies are essential for the platform to function. They cannot be disabled. They include:
      </p>
      <ul>
        <li>Authentication tokens (JWT) to keep you logged in</li>
        <li>Session cookies that expire when you close your browser</li>
        <li>Cookie consent preference storage</li>
        <li>CSRF protection tokens</li>
      </ul>
      <p><strong>Legal basis:</strong> Legitimate Interest (technically necessary)</p>

      <h3>2.2 Functional Cookies</h3>
      <p>
        These remember your preferences to enhance your experience. They can be disabled but this
        may affect some features:
      </p>
      <ul>
        <li>Language and display preferences</li>
        <li>Saved filter settings in lead views</li>
        <li>Dashboard layout preferences</li>
      </ul>
      <p><strong>Legal basis:</strong> Consent</p>

      <h3>2.3 Analytics Cookies</h3>
      <p>
        These help us understand how the platform is used so we can improve it. All data is
        aggregated and anonymised:
      </p>
      <ul>
        <li>Pages visited and features used</li>
        <li>Session duration and engagement metrics</li>
        <li>Error tracking and performance monitoring</li>
      </ul>
      <p><strong>Legal basis:</strong> Consent</p>

      <h3>2.4 Payment / Third-Party Cookies</h3>
      <p>
        These are set by Razorpay during the payment process to enable secure transaction processing.
        They are strictly necessary for payment functionality and are governed by Razorpay's
        own privacy and cookie policies.
      </p>
      <p><strong>Legal basis:</strong> Contractual Necessity</p>
    </div>

    <div className="legal-section" id="cookie-table" data-section>
      <h2>3. Cookie List</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Cookie Name</th>
            <th>Type</th>
            <th>Purpose</th>
            <th>Expiry</th>
            <th>Provider</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>crmautomax_token</td>
            <td>Strictly Necessary</td>
            <td>User authentication JWT token</td>
            <td>Session / 7 days</td>
            <td>CRM Automax</td>
          </tr>
          <tr>
            <td>crmautomax_cookie_consent</td>
            <td>Strictly Necessary</td>
            <td>Stores your cookie consent preferences</td>
            <td>12 months</td>
            <td>CRM Automax</td>
          </tr>
          <tr>
            <td>crmautomax_preferences</td>
            <td>Functional</td>
            <td>Dashboard layout and display preferences</td>
            <td>30 days</td>
            <td>CRM Automax</td>
          </tr>
          <tr>
            <td>_ga, _gid</td>
            <td>Analytics</td>
            <td>Google Analytics — usage statistics</td>
            <td>2 years / 24 hours</td>
            <td>Google</td>
          </tr>
          <tr>
            <td>rzp_* cookies</td>
            <td>Payment</td>
            <td>Razorpay payment session and fraud prevention</td>
            <td>Session</td>
            <td>Razorpay</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="legal-section" id="third-party" data-section>
      <h2>4. Third-Party Cookies</h2>
      <h3>4.1 Razorpay</h3>
      <p>
        When you make a payment, Razorpay sets cookies required for the secure processing of your
        transaction and fraud detection. These cookies are governed by Razorpay's privacy policy
        at <a href="https://razorpay.com/privacy/" target="_blank" rel="noreferrer">razorpay.com/privacy</a>.
      </p>
      <h3>4.2 Google Analytics</h3>
      <p>
        With your consent, we use Google Analytics to understand platform usage. Google Analytics
        anonymises IP addresses and does not allow us to identify individual users from this data.
        You can opt out of Google Analytics at any time via our cookie preference centre or by
        installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer">Google Analytics Opt-out Browser Add-on</a>.
      </p>
    </div>

    <div className="legal-section" id="manage" data-section>
      <h2>5. Managing Your Cookie Preferences</h2>
      <h3>5.1 Cookie Preference Centre</h3>
      <p>
        You can update your cookie preferences at any time by clicking the "Cookie Preferences"
        link in our footer. This opens our preference panel where you can enable or disable
        each cookie category independently.
      </p>
      <h3>5.2 Browser Settings</h3>
      <p>
        You can also manage cookies through your browser settings. Links to instructions for
        major browsers:
      </p>
      <ul>
        <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer">Google Chrome</a></li>
        <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noreferrer">Mozilla Firefox</a></li>
        <li><a href="https://support.apple.com/en-us/HT201265" target="_blank" rel="noreferrer">Apple Safari</a></li>
        <li><a href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noreferrer">Microsoft Edge</a></li>
      </ul>
      <p>
        Note: Disabling strictly necessary cookies (especially authentication tokens) will prevent
        you from logging in and using the platform.
      </p>
      <h3>5.3 Withdrawal of Consent</h3>
      <p>
        You may withdraw your consent for non-essential cookies at any time. Withdrawal of consent
        does not affect the lawfulness of processing based on consent before withdrawal.
        Non-essential cookies will be deleted from your browser upon withdrawal.
      </p>
    </div>

    <div className="legal-section" id="legal-basis" data-section>
      <h2>6. Legal Basis</h2>
      <p>
        Our use of cookies is governed by India's <strong>Digital Personal Data Protection Act (DPDPA) 2023</strong>.
        Where cookies process personal data, we rely on:
      </p>
      <ul>
        <li><strong>Consent</strong> — for analytics and functional cookies (freely given, specific, and withdrawable)</li>
        <li><strong>Legitimate Interest</strong> — for strictly necessary cookies essential to platform security</li>
        <li><strong>Contractual Necessity</strong> — for payment-related cookies required to process your transactions</li>
      </ul>
      <p>
        For EU/EEA visitors, our practices are also aligned with the GDPR and the ePrivacy Directive.
      </p>
    </div>

    <div className="legal-section" id="contact" data-section>
      <h2>7. Contact Us</h2>
      <p>For any questions about our use of cookies:</p>
      <table className="legal-table">
        <tbody>
          <tr><td><strong>Privacy Email</strong></td><td><a href="mailto:info@westernflex.com">info@westernflex.com</a></td></tr>
          <tr><td><strong>Support</strong></td><td><a href="mailto:info@westernflex.com">info@westernflex.com</a></td></tr>
        </tbody>
      </table>
    </div>
  </LegalPageLayout>
);

export default CookiePolicy;
